import { useState } from 'react';
import { Progress } from '../ui/Progress';
import { getCurrencySymbol } from '../../constants';
import { ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Multi-Currency Funding Display Component
 * Shows project funding total in base currency with optional currency breakdown
 */
export function MultiCurrencyFunding({ project, currencyBalances = [] }) {
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Calculate total in project's base currency
  const totalInBaseCurrency = parseFloat(project.current_funding || 0);
  const fundingGoal = parseFloat(project.funding_goal || 0);
  const fundingProgress = fundingGoal > 0 ? (totalInBaseCurrency / fundingGoal) * 100 : 0;

  // Check if there are multiple currencies
  const hasMultipleCurrencies = currencyBalances.length > 0;

  return (
    <div className="space-y-4">
      {/* Main Progress Bar */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <div>
            <span className="text-2xl font-bold text-neutral-900">
              {getCurrencySymbol(project.currency)}
              {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(totalInBaseCurrency)}
            </span>
            <span className="text-sm text-neutral-500 ml-2">raised</span>
          </div>
          <div className="text-right">
            <span className="text-sm text-neutral-600">
              of {getCurrencySymbol(project.currency)}
              {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(fundingGoal)}
            </span>
          </div>
        </div>

        <Progress value={Math.min(fundingProgress, 100)} className="h-3" />

        <div className="flex justify-between items-center mt-2">
          <p className="text-sm text-neutral-600">
            <TrendingUp className="w-4 h-4 inline mr-1" />
            {new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(fundingProgress)}% funded
          </p>
          {fundingProgress >= 100 && (
            <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
              ✓ Goal Reached!
            </span>
          )}
        </div>
      </div>

      {/* Currency Breakdown (Collapsible) */}
      {hasMultipleCurrencies && (
        <div className="border-t pt-3">
          <button
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="flex items-center justify-between w-full text-sm text-primary-teal hover:text-primary-teal/80 transition-colors"
          >
            <span className="font-medium">View donation breakdown by currency</span>
            {showBreakdown ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          <AnimatePresence>
            {showBreakdown && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-3 space-y-2"
              >
                {/* Pi donations - show if project currency is PI OR if there are actual Pi donations */}
                {(() => {
                  // Check if there are Pi donations in currency_balances
                  const piBalance = currencyBalances.find(b => b.currency === 'PI');
                  
                  // Show Pi section if project currency is PI OR if there are actual Pi donations
                  if ((project.currency === 'PI' && parseFloat(project.current_funding) > 0) || piBalance) {
                    const piAmount = piBalance ? piBalance.total_donated : project.current_funding;
                    return (
                      <div className="flex justify-between items-center p-3 bg-primary-teal/5 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-neutral-800">Pi (π)</p>
                          <p className="text-xs text-neutral-600">Cryptocurrency donations</p>
                        </div>
                        <span className="font-semibold text-neutral-900">
                          π{new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(piAmount)}
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Fiat donations by currency - exclude PI since shown above */}
                {currencyBalances.filter(balance => balance.currency !== 'PI').map(balance => (
                  <div
                    key={balance.currency}
                    className="flex justify-between items-center p-3 bg-green-50 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-neutral-800">
                        {balance.currency === 'NGN' ? 'Nigerian Naira' : balance.currency}
                      </p>
                      <p className="text-xs text-neutral-600">Fiat donations</p>
                    </div>
                    <span className="font-semibold text-neutral-900">
                      {getCurrencySymbol(balance.currency)}
                      {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(balance.total_donated)}
                    </span>
                  </div>
                ))}

                <div className="pt-2 border-t text-xs text-neutral-500">
                  <p>* All amounts are converted to {project.currency} for the progress bar</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export default MultiCurrencyFunding;